import { useEffect, useState } from "react";
import { prefersReducedMotion } from "@/lib/reveal-motion";
import { easeInOutSine } from "@/lib/sketch-stroke/sketch-easing";

/** Eased 0→1 pen progress for marks drawn locally. A new `key` draws again
 * from the start. Returns 1 when disabled, for a zero duration, or under
 * reduced motion. */
export function useSketchReveal({
  key,
  durationMs,
  enabled,
}: {
  key: string;
  durationMs: number;
  enabled: boolean;
}): number {
  const active = enabled && durationMs > 0 && !prefersReducedMotion();
  const [reveal, setReveal] = useState<{ key: string; progress: number }>({
    key: "",
    progress: 0,
  });

  useEffect(() => {
    if (!active) return;
    // Timed on the frame clock alone: the first frame starts the pen.
    let startedAt: number | null = null;
    let frame = 0;
    const tick = (now: number) => {
      startedAt ??= now;
      const linear = Math.min(1, (now - startedAt) / durationMs);
      setReveal({ key, progress: easeInOutSine(linear) });
      if (linear < 1) frame = window.requestAnimationFrame(tick);
    };
    setReveal({ key, progress: 0 });
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [active, durationMs, key]);

  if (!active) return 1;
  return reveal.key === key ? reveal.progress : 0;
}
